import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Calendar, User, Shield, Plus, Newspaper, CalendarDays
} from 'lucide-react';
import Navbar from '../components/Navbar';
import BookingModal from '../components/BookingModal';
import NewsModule from '../components/NewsModule';
import CalendarView from '../components/CalendarView';
import BookingsTab from '../components/BookingsTab';
import ProfileTab from '../components/ProfileTab';
import AdminTab from '../components/AdminTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('news');
  const [showBookingModal, setShowBookingModal] = useState(false);

  // User bookings (shared between BookingsTab and refresh on modal close)
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  const fetchBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/user/bookings`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBookings(data.bookings || []);
      }
    } catch (e) {
      /* fetch error - logged server-side */
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchBookings();
  }, [user, authLoading, navigate, fetchBookings]);

  if (authLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center pt-20">
          <div className="animate-spin w-8 h-8 border-4 border-[#1a237e] border-t-transparent rounded-full" />
        </div>
      </>
    );
  }

  const tabs = [
    { id: 'news', label: t('news.title'), icon: Newspaper },
    { id: 'bookings', label: t('dashboard.myBookings'), icon: Calendar },
    { id: 'calendar', label: t('dashboard.calendar'), icon: CalendarDays },
    { id: 'newBooking', label: t('dashboard.newBooking'), icon: Plus },
    { id: 'profile', label: t('dashboard.profile'), icon: User },
  ];
  if (isAdmin) tabs.push({ id: 'admin', label: t('dashboard.adminPanel'), icon: Shield });

  const handleNewBooking = () => {
    setActiveTab('newBooking');
    setShowBookingModal(true);
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome */}
          <div data-testid="dashboard-welcome" className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0d1442]">
              {t('dashboard.welcome')}, {user?.name || user?.email}
            </h1>
            {isAdmin && <span className="inline-block mt-2 px-3 py-1 text-xs font-semibold bg-[#1a237e] text-white rounded-full">Admin</span>}
          </div>

          {/* Tab Navigation */}
          <div data-testid="dashboard-tabs" className="flex flex-wrap gap-2 mb-8 border-b border-slate-200 pb-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-testid={`dashboard-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all text-sm ${
                  activeTab === tab.id
                    ? 'bg-[#1a237e] text-white shadow-md'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'news' && <NewsModule />}
          {activeTab === 'bookings' && <BookingsTab bookings={bookings} bookingsLoading={bookingsLoading} onNewBooking={handleNewBooking} />}
          {activeTab === 'calendar' && <CalendarView isAdmin={isAdmin} />}
          {activeTab === 'newBooking' && (
            <div data-testid="new-booking-tab" className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-[#1a237e]" />
              <h2 className="text-xl font-bold text-[#0d1442] mb-2">{t('dashboard.newBooking')}</h2>
              <p className="text-slate-500 mb-6">{t('dashboard.bookNow')}</p>
              <button data-testid="open-booking-modal-btn" onClick={() => setShowBookingModal(true)}
                className="px-8 py-3 bg-[#1a237e] text-white font-medium rounded-lg hover:bg-[#0d1442] transition-colors">
                <Plus className="w-5 h-5 inline mr-2" />{t('dashboard.bookNow')}
              </button>
            </div>
          )}
          {activeTab === 'profile' && <ProfileTab />}
          {activeTab === 'admin' && isAdmin && <AdminTab />}
        </div>
      </div>

      <BookingModal isOpen={showBookingModal} onClose={() => { setShowBookingModal(false); fetchBookings(); }} />
    </>
  );
};

export default DashboardPage;
